// Aura-driven site effects. Pure helpers consulted by the static-aura
// recompute pass. They count or transform sites under the source
// aura's covered set so the result can be fed into existing ledgers
// (mana, threshold).
//
// Today: `countCoveredSitesByController` for Abundance ("each affected
// site provides one additional mana"). Future site-override entries
// (Atlantean Fate, Flood, Drought) will add siblings here.

import { anchorCoveredCells } from '../../core/auraPlacement';

const RUBBLE_ID = 'sorcery-rubble';

/**
 * Count sites in the source aura's covered set, split by which player
 * controls them. Excludes Rubble (no affinity, doesn't count as a real
 * site for mana). Returns { allied, enemy } both zero when the source
 * isn't a placed aura.
 *
 * Per Sorcery rules, Abundance affects every covered site regardless
 * of controller. Each site provides the bonus mana to ITS controller,
 * not to the aura's controller. The split lets GameBoard route each
 * count to the right player's grant.
 */
export function countCoveredSitesByController(sourceCard, meshes, cellAt) {
  const empty = { allied: 0, enemy: 0 };
  if (!sourceCard?._auraShape || !sourceCard?._auraAnchor) return empty;
  const covered = new Set(anchorCoveredCells(sourceCard._auraAnchor, sourceCard._auraShape.shape));
  if (covered.size === 0) return empty;
  const sourceRotated = !!sourceCard.rotated;
  let allied = 0;
  let enemy = 0;
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    if (card.cardId === RUBBLE_ID) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    if (!covered.has(`${cell.col},${cell.row}`)) continue;
    if (!!card.rotated === sourceRotated) allied++;
    else enemy++;
  }
  return { allied, enemy };
}

/**
 * The mana each covered site PROVIDES, split by controller. The doubling
 * input for Sow the Earth ("This site provides double mana"). A site's own
 * provision is its base (1, unless "Provides no mana") plus its own printed
 * static, unconditional grant_mana (Shrine of the Dragonlord's +1). Doubling
 * grants an EXTRA equal to that total, so a covered Shrine ends at 4, not the
 * flat-+1's 3. Rubble (no affinity, no mana) is excluded like the count helper.
 *
 * Deliberately narrow: only unconditional static grant_mana is folded in;
 * conditional / ledger-dynamic mana grants are out of scope (rare on sites).
 */
export function sumCoveredSiteProvisionByController(sourceCard, meshes, cellAt, getFullCard) {
  const empty = { allied: 0, enemy: 0 };
  if (!sourceCard?._auraShape || !sourceCard?._auraAnchor) return empty;
  const covered = new Set(anchorCoveredCells(sourceCard._auraAnchor, sourceCard._auraShape.shape));
  if (covered.size === 0) return empty;
  const sourceRotated = !!sourceCard.rotated;
  let allied = 0;
  let enemy = 0;
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    if (card.cardId === RUBBLE_ID) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || !covered.has(`${cell.col},${cell.row}`)) continue;
    const full = getFullCard ? getFullCard(card.cardId) : null;
    let provision = full?.providesNoMana ? 0 : 1;
    // The site's OWN static grant_mana plus that of any Monument conjured atop it
    // (a Monument rides the site's equippedCards. Shrine of the Dragonlord's
    // "+ (1)"). Doubling the site's mana doubles the whole provision, so a
    // covered Shrine-bearing site reaches 4, not the flat-+1's 3.
    const sources = [full, ...((card.equippedCards || []).map((eq) => getFullCard && getFullCard(eq.cardId)))];
    for (const src of sources) {
      for (const e of (src?.automation || [])) {
        if (e.trigger !== 'static' || e.condition) continue;
        for (const eff of (e.effect || [])) {
          if (eff.op === 'grant_mana') provision += (eff.args?.amount || 0);
        }
      }
    }
    if (!!card.rotated === sourceRotated) allied += provision;
    else enemy += provision;
  }
  return { allied, enemy };
}
